// Catalog of Analytics charts embeddable in the "BI chart" home widget.
// Each entry knows its endpoint, its RBAC node, the Analytics tab to open, and
// how to render its own slice of data using the shared BI chart components,
// sized to the widget's measured content box so the chart always fills the tile.
// A chart marked `mtd: true` supports a Month-to-date view in addition to the
// default Year-to-date; the widget maps that choice to the /bi query params
// (YTD -> ?year=Y, MTD -> ?year=Y&month=<thisMonth>). Profit & Loss and Balance
// Sheet are intentionally excluded.
import type { ReactNode } from 'react';
import {
  MonthlyValueChart, DonutShare, OrderPipeline, BulletBar, RevenueCyPyChart, PnlMonthlyChart,
  Empty, withCumulative, linTrend, monthToDate, fmtK, FC_SERIES,
  type Filters, type DonutItem, type PipelineStatus,
  type SummaryData, type CustData, type ProdData, type SalesData, type PurchData,
} from '@/features/bi/shared';
import { RankList, type RankItem } from '@/components/ui/RankList';
import { formatMoney } from '@/shared/types/Money';
import { CHART } from '../homeStyles';

export type BiPeriod = 'ytd' | 'mtd';
export type BiChartGroup = 'Trends' | 'Rankings' | 'Breakdowns' | 'Gauges';
// The widget measures its content area and passes it in so every chart fits.
export type ChartBox = { width: number; height: number };

export type BiChartDef = {
  key: string;
  label: string;
  group: BiChartGroup;
  node: string;        // RBAC node the widget requires
  path: string;        // Analytics tab this chart lives on ("Open in Analytics")
  endpoint: string;    // /bi/... base; the period query string is appended
  mtd: boolean;        // exposes the YTD/MTD toggle in config
  render: (data: unknown, M: (n: number) => string, box: ChartBox) => ReactNode;
};

// Money formatter matching the tabs: currency symbol + compact K/M value.
export function moneyFmt(currency?: string): (n: number) => string {
  const sym = formatMoney(0, currency || 'USD').replace(/[\d.,\s]/g, '') || '$';
  return (n: number) => `${sym} ${fmtK(n)}`;
}

// Build the /bi query params for a period: YTD = whole current year to date,
// MTD = restrict to the current calendar month.
export function periodFilters(period: BiPeriod): Filters {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1; // 1-based for the API
  return {
    year, quarter: [], month: period === 'mtd' ? [month] : [],
    customer: '', vendor: '', product: '', status: '', branch: '', years: [],
  };
}

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));
const Fill = ({ box, children }: { box: ChartBox; children: ReactNode }) =>
  <div style={{ height: box.height, overflow: 'auto' }}>{children}</div>;
const EmptyMini = ({ box }: { box: ChartBox }) =>
  <div style={{ height: box.height }} className="flex items-center justify-center"><Empty compact /></div>;

// ---- reusable render helpers -------------------------------------------------
function monthlyChart(rows: Array<{ month: string; value: number }>, box: ChartBox): ReactNode {
  const data = withCumulative(rows ?? []);
  if (!data.length || data.every((d) => d.value === 0)) return <EmptyMini box={box} />;
  return <MonthlyValueChart data={data} color={CHART.series} M={fmtK} height={Math.max(120, box.height)} title="Monthly" tools={false} />;
}
function rankChart(items: RankItem[], M: (n: number) => string, box: ChartBox): ReactNode {
  if (!items.length) return <EmptyMini box={box} />;
  return <Fill box={box}><RankList items={items} formatValue={M} /></Fill>;
}
// Top-8 + "Others" donut from a revenue-ranked row set, sized to the box.
function shareDonut(rows: Array<{ name: string; revenue: number }>, caption: string, M: (n: number) => string, box: ChartBox): ReactNode {
  const ranked = (rows ?? []).filter((r) => r.revenue > 0).sort((a, b) => b.revenue - a.revenue);
  const total = ranked.reduce((s, r) => s + r.revenue, 0);
  if (!ranked.length || total <= 0) return <EmptyMini box={box} />;
  // Top-5 + Others (six slices: more cannot be told apart)
  const top = ranked.slice(0, 5);
  const items: DonutItem[] = top.map((r, i) => ({ label: r.name, value: r.revenue, pct: (r.revenue / total) * 100, color: FC_SERIES[i % FC_SERIES.length] }));
  const others = total - top.reduce((s, r) => s + r.revenue, 0);
  if (others > 0.5) items.push({ label: 'Others', value: others, pct: (others / total) * 100, color: '#94a3b8' });
  const size = clamp(Math.min(box.height - 6, box.width * 0.5), 90, 300);
  return <Fill box={box}><DonutShare items={items} size={size} centerValue={String(ranked.length)} centerCaption={caption} fmtValue={M} /></Fill>;
}
// A bullet bar centred in the widget (charts-06).
function bulletFit(node: ReactNode, box: ChartBox): ReactNode {
  return <div style={{ height: box.height }} className="flex flex-col justify-center px-2">{node}</div>;
}

// ---- the catalog -------------------------------------------------------------
export const BI_CHARTS: Record<string, BiChartDef> = {
  // Trends — monthly series.
  revenue_cy_py: {
    key: 'revenue_cy_py', label: 'Monthly Revenue — this year vs last', group: 'Trends',
    node: 'ledger.profit_loss', path: '/bi/summary', endpoint: '/bi/summary', mtd: false,
    render: (data, M, box) => {
      const d = data as SummaryData;
      const rows = d.revenue_cy_py ?? [];
      if (!rows.length) return <EmptyMini box={box} />;
      const t = linTrend(rows.map((x) => x.cy));
      const chart = rows.map((x, i) => ({ ...x, trend: t[i] }));
      return <RevenueCyPyChart data={chart} year={d.year} M={M} height={box.height} title="Monthly Revenue — this year vs last" tools={false} variant="lines" />;
    },
  },
  pnl_monthly: {
    key: 'pnl_monthly', label: 'Profit & Loss — Monthly', group: 'Trends',
    node: 'ledger.profit_loss', path: '/bi/summary', endpoint: '/bi/summary', mtd: false,
    render: (data, M, box) => {
      const d = data as SummaryData;
      const rows = d.trend ?? [];
      if (!rows.length || rows.every((r) => r.revenue === 0 && r.expense === 0)) return <EmptyMini box={box} />;
      return <PnlMonthlyChart data={rows} M={M} height={box.height} tools={false} />;
    },
  },
  revenue_monthly: {
    key: 'revenue_monthly', label: 'Revenue — monthly', group: 'Trends',
    node: 'ledger.profit_loss', path: '/bi/summary', endpoint: '/bi/summary', mtd: false,
    render: (data, _M, box) => monthlyChart((data as SummaryData).trend?.map((t) => ({ month: t.month, value: t.revenue })) ?? [], box),
  },
  sales_monthly: {
    key: 'sales_monthly', label: 'Sales — monthly', group: 'Trends',
    node: 'sales.orders', path: '/bi/sales', endpoint: '/bi/sales', mtd: false,
    render: (data, _M, box) => monthlyChart((data as SalesData).monthly ?? [], box),
  },
  purchasing_monthly: {
    key: 'purchasing_monthly', label: 'Purchasing — monthly', group: 'Trends',
    node: 'payables.purchase_orders', path: '/bi/purchasing', endpoint: '/bi/purchasing', mtd: false,
    render: (data, _M, box) => monthlyChart((data as PurchData).monthly ?? [], box),
  },

  // Rankings — top-N lists; YTD or MTD.
  top_customers: {
    key: 'top_customers', label: 'Top 10 Customers', group: 'Rankings',
    node: 'sales.customers', path: '/bi/customers', endpoint: '/bi/customers', mtd: true,
    render: (data, M, box) => rankChart((data as CustData).rows?.filter((r) => r.revenue > 0).slice(0, 10).map((x) => ({ id: x.id, label: x.name, value: x.revenue })) ?? [], M, box),
  },
  top_products: {
    key: 'top_products', label: 'Top 10 Products', group: 'Rankings',
    node: 'products.items', path: '/bi/products', endpoint: '/bi/products', mtd: true,
    render: (data, M, box) => rankChart((data as ProdData).rows?.filter((r) => r.revenue > 0).slice(0, 10).map((x) => ({ id: x.id, label: x.name, value: x.revenue })) ?? [], M, box),
  },
  top_vendors: {
    key: 'top_vendors', label: 'Top Vendors by spend', group: 'Rankings',
    node: 'payables.purchase_orders', path: '/bi/purchasing', endpoint: '/bi/purchasing', mtd: true,
    render: (data, M, box) => rankChart((data as PurchData).vendors?.slice(0, 10).map((x) => ({ id: x.id, label: x.name, value: x.total })) ?? [], M, box),
  },
  top_items: {
    key: 'top_items', label: 'Top Items by spend', group: 'Rankings',
    node: 'payables.purchase_orders', path: '/bi/purchasing', endpoint: '/bi/purchasing', mtd: true,
    render: (data, M, box) => rankChart((data as PurchData).top_items?.slice(0, 10).map((x, i) => ({ id: x.id ?? String(i), label: x.name, value: x.spend })) ?? [], M, box),
  },

  // Breakdowns — donut share / pipeline; YTD or MTD.
  customer_share: {
    key: 'customer_share', label: 'Customer share', group: 'Breakdowns',
    node: 'sales.customers', path: '/bi/customers', endpoint: '/bi/customers', mtd: true,
    render: (data, M, box) => shareDonut((data as CustData).rows ?? [], 'customers', M, box),
  },
  product_share: {
    key: 'product_share', label: 'Product revenue share', group: 'Breakdowns',
    node: 'products.items', path: '/bi/products', endpoint: '/bi/products', mtd: true,
    render: (data, M, box) => shareDonut((data as ProdData).rows ?? [], 'products', M, box),
  },
  sales_pipeline: {
    key: 'sales_pipeline', label: 'Sales order pipeline', group: 'Breakdowns',
    node: 'sales.orders', path: '/bi/sales', endpoint: '/bi/sales', mtd: true,
    render: (data, M, box) => {
      const bs = (data as SalesData).by_status ?? [];
      const totalN = bs.reduce((s, x) => s + x.n, 0) || 1;
      const totalAmt = bs.reduce((s, x) => s + x.value, 0) || 1;
      if (!bs.length) return <EmptyMini box={box} />;
      const statuses: PipelineStatus[] = bs.map((s, i) => ({ label: s.status, value: s.value, pct: (s.n / totalN) * 100, count: s.n, color: FC_SERIES[i % FC_SERIES.length] }));
      return <Fill box={box}><OrderPipeline statuses={statuses} total={totalAmt} fmtValue={M} fmtNum={(n) => String(Math.round(n))} /></Fill>;
    },
  },
  projects_status: {
    key: 'projects_status', label: 'Projects by status', group: 'Breakdowns',
    node: 'projects.projects', path: '/bi/projects', endpoint: '/bi/projects', mtd: true,
    render: (data, _M, box) => {
      const d = data as { statusMix?: Array<{ status: string; count: number }>; kpis?: { project_count?: number } };
      const total = d.kpis?.project_count ?? (d.statusMix ?? []).reduce((s, x) => s + x.count, 0);
      const items: DonutItem[] = (d.statusMix ?? []).map((s, i) => ({ label: s.status.replace(/_/g, ' '), value: s.count, pct: total ? (s.count / total) * 100 : 0, color: FC_SERIES[i % FC_SERIES.length] }));
      if (!items.length) return <EmptyMini box={box} />;
      const size = clamp(Math.min(box.height - 6, box.width * 0.5), 90, 300);
      return <Fill box={box}><DonutShare items={items} size={size} centerValue={String(total)} centerCaption="projects" fmtValue={(n) => String(Math.round(n))} /></Fill>;
    },
  },

  // Gauges — annual/target semantics are their own period, so no MTD toggle.
  gauge_annual: {
    key: 'gauge_annual', label: 'Annual cumulative (vs last year)', group: 'Gauges',
    node: 'ledger.profit_loss', path: '/bi/summary', endpoint: '/bi/summary', mtd: false,
    render: (data, M, box) => {
      const cy = (data as SummaryData).revenue_cy_py ?? [];
      const annualCy = cy.reduce((s, x) => s + x.cy, 0);
      const annualPy = cy.reduce((s, x) => s + x.py, 0);
      return bulletFit(<BulletBar value={annualCy} target={annualPy} fmt={M} label="Annual cumulative" />, box);
    },
  },
  gauge_month: {
    key: 'gauge_month', label: 'Month to date (vs same days last year)', group: 'Gauges',
    node: 'ledger.profit_loss', path: '/bi/summary', endpoint: '/bi/summary', mtd: false,
    render: (data, M, box) => {
      const cy = (data as SummaryData).revenue_cy_py ?? [];
      const m = monthToDate(cy, new Date());
      return bulletFit(<BulletBar value={m.cy} target={m.pyProRata} fmt={M} label="Month to date" />, box);
    },
  },
  gauge_target: {
    key: 'gauge_target', label: 'Sales target', group: 'Gauges',
    node: 'ledger.profit_loss', path: '/bi/summary', endpoint: '/bi/summary', mtd: false,
    render: (_data, _M, box) => bulletFit(<div className="text-center"><div className="text-body text-primary-text">No sales target set</div></div>, box),
  },
};

export const BI_GROUP_ORDER: BiChartGroup[] = ['Trends', 'Rankings', 'Breakdowns', 'Gauges'];
export const DEFAULT_BI_CHART = 'sales_monthly';
